import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';
import { cssMap } from '@compiled/react';

import FeedbackIcon from '@atlaskit/icon/core/feedback';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Pressable, Inline, xcss } from '@atlaskit/primitives';

import { FeedbackType } from '@atlassian/company-hub-common';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled';

import { useFeedbackCollector } from './useFeedbackCollector';

const i18n = defineMessages({
	shareFeedbackButtonLabel: {
		id: 'company-hub-utils.share-feedback-button.label',
		defaultMessage: 'Share feedback on Company hub',
		description: 'CTA button text for users to provide feedback on their Company hub experience',
	},
});

const layoutStyles = xcss({
	position: 'fixed',
	margin: 'space.050',
	bottom: 'space.150',
});

const leftPosition = cssMap({
	none: { left: 'space.150' },
	isNav4: { left: 'calc(space.150 + var(--leftSidebarWidth, 0px))' },
});

const buttonStyles = xcss({
	borderRadius: 'radius.small',
	backgroundColor: 'elevation.surface',
	boxShadow: 'elevation.shadow.overlay',
	color: 'color.text',
	paddingBlock: 'space.050',
	paddingInline: 'space.100',
	display: 'flex',
	columnGap: 'space.050',
	alignItems: 'center',
	':hover': {
		cursor: 'pointer',
		backgroundColor: 'elevation.surface.overlay.hovered',
	},
	':active': {
		backgroundColor: 'elevation.surface.overlay.pressed',
	},
});

export const ShareFeedbackButtonInner = ({ isNav4Enabled }: { isNav4Enabled: boolean }) => {
	const isNav4: string = isNav4Enabled ? 'isNav4' : 'none';

	const openFeedbackCollector = useFeedbackCollector({
		source: 'companyHubViewPage',
		type: FeedbackType.COMPANY_HUB,
	});

	return (
		<Inline testId="layoutContainer" xcss={[layoutStyles, leftPosition[isNav4]]}>
			<Pressable xcss={buttonStyles} onClick={openFeedbackCollector}>
				<FeedbackIcon label="" spacing="spacious" />
				<FormattedMessage {...i18n.shareFeedbackButtonLabel} />
			</Pressable>
		</Inline>
	);
};

const ShareFeedbackButtonComponent = () => {
	const isNav4Enabled = useIsNav4Enabled();

	return <ShareFeedbackButtonInner isNav4Enabled={isNav4Enabled} />;
};

export const ShareFeedbackButton = withTransparentErrorBoundary({
	attribution: Attribution.COMPANY_HUB,
})(ShareFeedbackButtonComponent);
